import { getBackendUrl } from '../env'
import { useDevicesContext } from '../hooks/useDevicesContext'
import { useAuthContext } from '../hooks/useAuthContext'
import { formatDistanceToNow } from 'date-fns'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    getDeviceReading,
    getDeviceReadingsSparkline,
    removeDeviceFromSystem,
} from '../helper/deviceHelper'
import { getSystemById } from '../helper/environmentHelper'
import { Device } from '../context/DeviceContext'
import DeleteAlert from './DeleteAlert'
import { Settings, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { MetricSparkline } from './systemMetrics/MetricSparkline'
import {
    classifyDeviceType,
    formatMetricValue,
    parseReadingToDisplayNumber,
    unitForCategory,
    type MetricCategory,
} from './systemMetrics/metricHelpers'

const MAX_SPARKLINE_POINTS = 36

interface DeviceDetailsProps {
    device: Device
    setDevice: React.Dispatch<React.SetStateAction<Device | null>>
    setAddDevicePopup: React.Dispatch<React.SetStateAction<boolean>>
    setNewDevice: React.Dispatch<React.SetStateAction<boolean>>
}

function isSensorHardware(device: Device): boolean {
    const t = device.device_type
    return !t.includes('Pump') && !t.includes('Outlet')
}

const DevicesDetails = ({
    device,
    setDevice,
    setAddDevicePopup,
    setNewDevice,
}: DeviceDetailsProps) => {
    const { dispatch: dispatchDevice } = useDevicesContext()
    const { user } = useAuthContext()
    const [openDeleteAlert, setOpenDeleteAlert] = useState(false)
    const [displayValue, setDisplayValue] = useState<number | null>(null)
    const [readingHistory, setReadingHistory] = useState<number[]>([])
    const [lastReadingAt, setLastReadingAt] = useState<Date | null>(null)
    const [loadingReadings, setLoadingReadings] = useState(false)
    const mountedRef = useRef(true)

    const category: MetricCategory = classifyDeviceType(device.device_type)
    const unit = unitForCategory(category)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const handleSettings = () => {
        setDevice(device)
        setAddDevicePopup(true)
        setNewDevice(false)
    }

    const requestDeviceDeletionFromSystem = async () => {
        if (user && device.system_id && device.system_id !== '') {
            const system = await getSystemById(user, device.system_id)
            if (system) {
                await removeDeviceFromSystem(user, system, device)
            }
        }
    }

    const handleDelete = async () => {
        if (!user) return
        if (device.system_id) {
            await requestDeviceDeletionFromSystem()
        }
        const response = await fetch(`${getBackendUrl()}/api/devices/` + device._id, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${user.token}`,
            },
        })
        const json = await response.json()

        if (response.ok) {
            dispatchDevice({ type: 'DELETE_DEVICE', payload: json })
        }
    }

    const applyReadingToDisplay = useCallback(
        (rawReading: string, timestamp: string | Date) => {
            const v = parseReadingToDisplayNumber(String(rawReading), category)
            setDisplayValue(v)
            if (v !== null) {
                setLastReadingAt(new Date(timestamp))
            }
        },
        [category]
    )

    const appendReadingToHistory = useCallback(
        (rawReading: string) => {
            const v = parseReadingToDisplayNumber(String(rawReading), category)
            if (v === null) return
            setReadingHistory((prev) => {
                const next = [...prev, v]
                return next.length > MAX_SPARKLINE_POINTS
                    ? next.slice(next.length - MAX_SPARKLINE_POINTS)
                    : next
            })
        },
        [category]
    )

    const fetchLatestReading = useCallback(
        async (opts?: { appendToHistory?: boolean }) => {
            const appendToHistory = opts?.appendToHistory === true
            if (!user || !isSensorHardware(device)) return
            setLoadingReadings(true)
            const result = await getDeviceReading(user, device.device_id)
            if (!mountedRef.current) return
            if (result.reading !== '--') {
                applyReadingToDisplay(String(result.reading), result.timestamp)
                if (appendToHistory) {
                    appendReadingToHistory(String(result.reading))
                }
            } else {
                setDisplayValue(null)
            }
            setLoadingReadings(false)
        },
        [user, device.device_id, device.device_type, applyReadingToDisplay, appendReadingToHistory]
    )

    const loadSparkline = useCallback(async () => {
        if (!user || !isSensorHardware(device)) return
        const res = await getDeviceReadingsSparkline(user, device.device_id, {
            limit: MAX_SPARKLINE_POINTS,
            hours: 72,
        })
        if (!mountedRef.current) return
        if (res && 'values' in res && Array.isArray(res.values)) {
            const values = res.values
                .map((v) => parseReadingToDisplayNumber(String(v), category))
                .filter((n): n is number => n !== null)
            if (values.length > 0) {
                setReadingHistory(values)
            }
        }
    }, [user, device.device_id, device.device_type, category])

    useEffect(() => {
        if (!isSensorHardware(device)) {
            setDisplayValue(null)
            setReadingHistory([])
            setLastReadingAt(null)
            return
        }
        void loadSparkline()
        void fetchLatestReading({ appendToHistory: false })
        const intervalId = setInterval(() => {
            void fetchLatestReading({ appendToHistory: true })
        }, 60000)
        return () => clearInterval(intervalId)
    }, [device._id, device.device_id, device.device_type, loadSparkline, fetchLatestReading])

    const systemLabel =
        device.system_id && device.system_id !== ''
            ? device.system && device.system !== ''
                ? device.system
                : 'Linked system'
            : null

    return (
        <div
            className={cn(
                'flex min-w-0 w-full max-w-lg flex-col gap-3 rounded-xl border border-zinc-700/90 bg-zinc-950/80 p-4 shadow-lg ring-1 ring-zinc-800/60'
            )}
        >
            <div className="flex flex-col gap-2 border-b border-zinc-800/90 pb-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h4 className="truncate text-lg font-semibold tracking-tight text-zinc-50">
                            {device.name}
                        </h4>
                        <p className="truncate text-xs text-zinc-500">{device.device_type}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                            onClick={handleSettings}
                            aria-label="Device settings"
                        >
                            <Settings className="size-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-zinc-400 hover:bg-red-900/40 hover:text-red-200"
                            onClick={() => setOpenDeleteAlert(true)}
                            aria-label="Delete device"
                        >
                            <Trash2 className="size-4" />
                        </Button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                    <span
                        className={cn(
                            'rounded-full border px-2 py-0.5 font-medium',
                            device.connected
                                ? 'border-emerald-500/40 bg-emerald-950/50 text-emerald-200'
                                : 'border-zinc-600 bg-zinc-900 text-zinc-400'
                        )}
                    >
                        {device.connected ? 'Connected' : 'Offline'}
                    </span>
                    {systemLabel ? (
                        <span className="rounded-full border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 text-zinc-400">
                            {systemLabel}
                        </span>
                    ) : (
                        <span className="rounded-full border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 text-zinc-500">
                            Unassigned
                        </span>
                    )}
                </div>

                <p className="text-[11px] text-zinc-500">
                    Added {formatDistanceToNow(new Date(device.createdAt), { addSuffix: true })}
                </p>
            </div>

            {isSensorHardware(device) ? (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                                Current reading
                            </p>
                            <div className="flex flex-wrap items-end gap-x-2 gap-y-0">
                                <span className="text-3xl font-semibold tabular-nums tracking-tight text-zinc-50">
                                    {loadingReadings && displayValue === null
                                        ? '…'
                                        : displayValue === null
                                          ? '—'
                                          : formatMetricValue(displayValue, category)}
                                </span>
                                {unit ? (
                                    <span className="pb-1 text-sm font-medium text-zinc-500">{unit}</span>
                                ) : null}
                            </div>
                        </div>
                        <MetricSparkline values={readingHistory} stroke="#34d399" />
                    </div>
                    <p className="text-[11px] text-zinc-500">
                        {lastReadingAt
                            ? `Reading ${formatDistanceToNow(lastReadingAt, { addSuffix: true })}`
                            : 'No reading yet'}
                    </p>
                </div>
            ) : (
                <p className="text-sm leading-relaxed text-zinc-400">
                    Pumps and outlets are configured in <strong className="text-zinc-300">Settings</strong>{' '}
                    (roles, calibration, and schedules).
                </p>
            )}

            <DeleteAlert
                openDeleteAlert={openDeleteAlert}
                setOpenDeleteAlert={setOpenDeleteAlert}
                handleDelete={handleDelete}
                titleMessage="Delete device"
                message={
                    'Are you sure you want to delete ' +
                    device.name +
                    '? This cannot be undone.'
                }
            />
        </div>
    )
}

export default DevicesDetails
